import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { clone } from 'lodash-es';
import { Subscription } from 'rxjs';
import { NoWhitespacePipe } from 'src/app/core/pipes/no-whitespace.pipe';
import { AuthService } from 'src/app/core/services/auth.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { MemberInformationService } from 'src/app/core/services/member-information.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { MEMBER_HOME_LABEL, SideNavService } from 'src/app/core/services/side-nav.service';
import { UserService } from 'src/app/core/services/user.service';
import { ExternalNavItem, SecondaryNavItem } from 'src/app/core/utils/routing-util';
import { isString } from 'src/app/core/utils/type-util';
import { EditMyProfileComponent } from '../edit-my-profile/edit-my-profile.component';
import { FeedbackComponent } from '../feedback/feedback.component';
import { UnreadMessageCountComponent } from '../messenger/unread-message-count/unread-message-count.component';
import { UnreadNotificationCountComponent } from '../notifications/unread-notification-count/unread-notification-count.component';

@Component({
  selector: 'th-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  imports: [
    NgbTooltip,
    NgClass,
    RouterLinkActive,
    RouterLink,
    UnreadNotificationCountComponent,
    UnreadMessageCountComponent,
    EditMyProfileComponent,
    FeedbackComponent,
    AsyncPipe
  ]
})
export class SideNavComponent implements OnInit, OnDestroy {
  isHoveringPrimaryNav = false;
  links: Record<string, { id: string; title: string }>;
  externalNavItems: ExternalNavItem[];
  patientNav: SecondaryNavItem[];
  adminHubNav: SecondaryNavItem[];
  externalNavItemsSubscription: Subscription;
  patientNavSubscription: Subscription;
  memberIdSubscription: Subscription;
  adminHubNavSubscription: Subscription;
  asmStatusUpdatedSubscription: Subscription;
  refreshHeaderTextSubscription: Subscription;
  memberId: string;
  loadingPatient = false;

  get showPatientNav(): boolean {
    return this.patientNav && this.patientNav.length && !this.isHoveringPrimaryNav;
  }

  get showAdminHubNav(): boolean {
    return this.adminHubNav && this.adminHubNav.length && !this.isHoveringPrimaryNav;
  }

  constructor(
    public authService: AuthService,
    public sideNavService: SideNavService,
    public router: Router,
    private headerService: HeaderService,
    private memberInformationService: MemberInformationService,
    private patientService: PatientService,
    private userService: UserService,
    private modalService: NgbModal,
    private noWhitespacePipe: NoWhitespacePipe
  ) {
    this.initializePrimaryNavLinks();
  }

  ngOnInit() {
    this.externalNavItemsSubscription = this.sideNavService.externalNavItems.subscribe((externalNavItems) => {
      this.externalNavItems = externalNavItems;
    });

    this.patientNavSubscription = this.sideNavService.patientNav.subscribe((patientNav) => {
      this.patientNav = patientNav;

      this.memberIdSubscription?.unsubscribe();
      this.memberIdSubscription = this.memberInformationService.memberId.subscribe((memberId) => {
        this.memberId = memberId;

        this.updateHeaderText();
      });

      this.refreshHeaderTextSubscription?.unsubscribe();
      this.refreshHeaderTextSubscription = this.headerService.refreshCenterContentText.subscribe(() => {
        this.updateHeaderText();
      });
    });

    this.adminHubNavSubscription = this.sideNavService.adminHubNav.subscribe((adminHubNav) => {
      this.adminHubNav = adminHubNav;
    });

    this.asmStatusUpdatedSubscription?.unsubscribe();

    this.asmStatusUpdatedSubscription = this.userService.userASMStatusUpdate.subscribe(() => {
      this.userService.getUser(this.authService.user.userId, false).subscribe((data) => {
        this.authService.user.asmStatus = clone(data).asmStatus;
      });
    });
  }

  ngOnDestroy() {
    this.externalNavItemsSubscription?.unsubscribe();
    this.patientNavSubscription?.unsubscribe();
    this.adminHubNavSubscription?.unsubscribe();
    this.memberIdSubscription?.unsubscribe();
    this.asmStatusUpdatedSubscription?.unsubscribe();
  }

  onPrimaryNavMouseEnter() {
    this.isHoveringPrimaryNav = true;
  }

  onPrimaryNavMouseLeave() {
    this.isHoveringPrimaryNav = false;
  }

  isEntitled(navItem: SecondaryNavItem): boolean {
    if (isString(navItem.entitlementUrl)) {
      return this.authService.isEntitled(navItem.entitlementUrl);
    } else if (navItem.entitlementUrl != null) {
      return navItem.entitlementUrl(this.authService);
    }

    return true;
  }

  updateHeaderText(secondaryNavItem: SecondaryNavItem = null) {
    const currentPatientNavItem = this.patientNav.find((navItem) => {
      if (secondaryNavItem) {
        return secondaryNavItem.routerLink === navItem.routerLink;
      }

      return navItem.routerLink === this.router.url;
    });

    if (this.memberId) {
      this.patientService.getPatientName(this.memberId, true).subscribe((patientName) => {
        if (currentPatientNavItem == null || currentPatientNavItem.displayText === MEMBER_HOME_LABEL) {
          this.headerService.setCenterContentText(patientName);

          this.patientService.getPatientPronoun(this.memberId).subscribe((preferredPronoun) => {
            this.headerService.setPronounText(preferredPronoun);
          });
        } else {
          this.headerService.setCenterContentText(`${patientName} / ${currentPatientNavItem.displayText}`);

          this.patientService.getPatientPronoun(this.memberId).subscribe((preferredPronoun) => {
            this.headerService.setPronounText(preferredPronoun);
          });
        }
      });
    } else {
      this.headerService.setCenterContentText('');
      this.headerService.setPronounText('');
    }
  }

  openModal(templateRef: TemplateRef<unknown>) {
    this.modalService.open(templateRef, { size: 'lg' });
  }

  private initializePrimaryNavLinks() {
    const links: Record<string, { id?: string; title: string }> = {
      myDashboard: {
        title: 'My Dashboard'
      },
      myWorklists: {
        title: 'My Worklists'
      },
      associatedPatients: {
        title: 'Associated Patients'
      },
      dmDashboard: {
        title: 'Programs Dashboard'
      },
      searchMembers: {
        title: 'Search Members'
      },
      bulkAssignMembers: {
        title: 'Bulk Assign Members'
      },
      manualPatientEntry: {
        title: 'Manual Patient Entry'
      },
      users: {
        title: 'Users'
      },
      reports: {
        title: 'Reports'
      },
      resources: {
        title: 'Resources'
      },
      adminHub: {
        title: 'Admin Hub'
      },
      manageResources: {
        title: 'Manage Resources'
      },
      maintainAssessments: {
        title: 'Maintain Assessments'
      },
      configureMessages: {
        title: 'Configure Messages'
      },
      configureAppNotifications: {
        title: 'Configure Application Notifications'
      },
      maintainWorklists: {
        title: 'Maintain Worklists'
      },
      messenger: {
        title: 'Messenger'
      },
      support: {
        title: 'Support'
      },
      requestNewMHNConnectUser: {
        title: 'Request New MHNConnect User'
      },
      updateProfile: {
        title: 'Account Settings'
      },
      userFeedback: {
        title: 'Feedback'
      },
      duplicateMembers: {
        title: 'Duplicate Members'
      },
      systemTest: {
        title: 'System Test'
      },
      reportFiles: {
        title: 'Sftp Report Files'
      },
      logout: {
        title: 'Logout'
      },
      notifications: {
        title: 'Notifications'
      }
    };

    Object.keys(links).forEach((key) => {
      links[key].id = 'Nav-Primary-' + this.noWhitespacePipe.transform(links[key].title);
    });

    this.links = links as Record<string, { id: string; title: string }>;
  }
}
